/**
 * Conferma di avvio della rotazione generale (F-078).
 *
 * Un modal e non un click diretto perché la rotazione **scrive**: i titoli
 * eletti entrano in watchlist, e volendo anche in wishlist con dei limiti di
 * esposizione. Sono conseguenze che vanno viste prima, non scoperte dopo.
 *
 * La spunta della wishlist non è preselezionata: candidare all'acquisto è un
 * gesto in più rispetto a mettere sotto osservazione, e va chiesto — non
 * dedotto da un pulsante che diceva un'altra cosa. Stessa regola già adottata
 * per gli inneschi di auto-trading (F-057).
 */

import { useState, type FormEvent } from 'react';
import { Alert, Button, Field, Modal, Select, Switch } from '@/ui';
import { usePortfolios } from '@/features/portfolios/api';
import type { StartRotationInput } from './api';

type Props = {
  identified: number;
  pending: boolean;
  error: string | null;
  onConfirm: (input: StartRotationInput) => void;
  onClose: () => void;
};

export function RotationConfirmModal({ identified, pending, error, onConfirm, onClose }: Props) {
  const portfolios = usePortfolios();
  const [addToWishlist, setAddToWishlist] = useState(false);
  const [portfolioId, setPortfolioId] = useState('');
  const [maxExposureAmount, setMaxExposureAmount] = useState('');
  const [maxPortfolioPercentage, setMaxPortfolioPercentage] = useState('');

  // Il numero di eletti è una funzione dell'ampiezza della ricerca: dirlo
  // prima evita la domanda «quanti me ne aggiunge?» dopo.
  const expected = Math.max(1, Math.round(identified / 5));
  const options = (portfolios.data?.items ?? []).map((portfolio) => ({
    value: portfolio.id,
    label: `${portfolio.name} (${portfolio.baseCurrency})`,
  }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onConfirm({
      addToWishlist,
      ...(addToWishlist ? { portfolioId } : {}),
      ...(addToWishlist && maxExposureAmount.trim() ? { maxExposureAmount: maxExposureAmount.trim() } : {}),
      ...(addToWishlist && maxPortfolioPercentage.trim()
        ? { maxPortfolioPercentage: maxPortfolioPercentage.trim() }
        : {}),
    });
  };

  return (
    <Modal title="Avvia la rotazione generale" onClose={onClose}>
      <form onSubmit={submit} className="rotation-confirm">
        <p>
          Confronterò i <strong>{identified} titoli identificati</strong> a gruppi di cinque e ne
          eleggerò circa <strong>{expected}</strong> — quelli con più potenziale{' '}
          <em>da osservare</em>, non da comprare.
        </p>
        <p className="cell--minor">
          I titoli eletti verranno aggiunti alla <strong>watchlist</strong> con il motivo della scelta e
          la tematica di partenza.
        </p>

        <div className="rotation-confirm__wishlist">
          <Switch
            label="Aggiungi anche alla wishlist"
            checked={addToWishlist}
            onChange={setAddToWishlist}
          />
          <p className="cell--minor">
            Li candida all&apos;acquisto su un portafoglio, con i limiti che indichi qui sotto applicati
            a tutti quelli scelti. Nelle note per l&apos;AI finiranno la tematica e il motivo, in breve.
          </p>
        </div>

        {addToWishlist && (
          <div className="rotation-confirm__fields">
            <Select
              label="Portafoglio di destinazione"
              value={portfolioId}
              onChange={(event) => setPortfolioId(event.target.value)}
              options={options}
              placeholder="Scegli un portafoglio"
              required
            />
            <div className="discovery-reconcile__manual-row">
              <Field
                label="Esposizione massima"
                value={maxExposureAmount}
                onChange={(event) => setMaxExposureAmount(event.target.value)}
                placeholder="5000"
                numeric
                hint="Nella valuta del portafoglio. Facoltativo."
              />
              <Field
                label="Peso massimo"
                value={maxPortfolioPercentage}
                onChange={(event) => setMaxPortfolioPercentage(event.target.value)}
                placeholder="5"
                numeric
                hint="% del portafoglio. Facoltativo."
              />
            </div>
          </div>
        )}

        {error && (
          <Alert variant="error" title="Rotazione non avviata">
            {error}
          </Alert>
        )}

        <div className="discovery-reconcile__actions">
          <Button variant="ghost" type="button" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" disabled={pending || (addToWishlist && !portfolioId)}>
            {pending ? 'Avvio…' : 'Avvia rotazione'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
